<template>
  <div :class="['relative inline-block', props.width]">
    <label
      v-if="$slots.label"
      :for="finalId"
      :class="[
        { 'mb-1 block font-semibold': labeltop},
        { 'absolute -mt-3 ml-4 text-sm': labelup && labelinside },
        { 'absolute text-petrol-500 font-semibold mt-[0.4rem] ml-11 text-sm': label && labelinside && $slots.icon && !ruesSearchbar && !searchresultSearchbar},
        { 'absolute text-petrol-500 font-semibold mt-[0.4rem] ml-4 text-sm': label && labelinside && !$slots.icon && !ruesSearchbar && !searchresultSearchbar},
        { 'absolute text-neutral-900 font-normal ml-9 left-0 text-lg scale-[80%]': label && labelinside && $slots.icon && (ruesSearchbar || searchresultSearchbar)},
        { 'absolute text-neutral-900 font-normal ml-4 left-0 text-lg scale-[80%]': label && labelinside && !$slots.icon && (ruesSearchbar || searchresultSearchbar)},
        { 'mt-2': (ruesSearchbar || searchresultSearchbar)},
        { 'lg:mt-0': searchresultSearchbar},
      ]"
    >
      <slot name="label" />
    </label>
    <template v-if="$slots.icon">
      <span class="absolute top-0 left-0 py-2 h-full flex items-center ml-3">
        <slot name="icon" />
      </span>
    </template>
    <select
      :id="finalId"
      ref="select"
      v-bind="{ title: props.title || undefined }"
      :name="name"
      :aria-label="props.ariaLabel"
      :value="modelValue"
      :class="[
        { 'border-cta-700 bg-cta-50': props.error && props.errorLevel === 'error' },
        { 'border-neutral-200 bg-white': !props.error && !greypill && !ruesSearchbar && !searchresultSearchbar },
        { 'border-petrol-500 bg-petrol-50': props.error && props.errorLevel === 'info' },
        { 'custom-select border-neutral-200 bg-neutral-25 rounded-full border focus:border-neutral-200 focus:ring-1 focus:ring-transparent font-semibold text-left focus:outline-hidden custom-select-fontsize cursor-pointer': greypill },
        { 'custom-select border text-left custom-select-fontsize cursor-pointer rounded-xl text-lg': normal || slim || xs },
        { 'pl-11 pr-8': $slots.icon && !ruesSearchbar },
        { 'pl-4 pr-8': !$slots.icon && !ruesSearchbar },
        { 'pt-6 pb-1 font-semibold rounded-xl min-h-[55px]': props.label && normal },
        { 'py-0 rounded-md': xs },
        { 'py-2': slim },
        { 'py-3 min-h-[46px] rounded-full focus:outline-hidden focus:border-petrol-500 focus:ring-0 text-sm': pill },
        { 'py-3 min-h-[55px] rounded-lg': normal && !props.label },
        { 'select-active': isActive && (normal || slim || xs) },
        { 'select-no-active': !isActive && (normal || slim || xs) },
        { 'pl-11 pr-8': $slots.icon && (ruesSearchbar || searchresultSearchbar) },
        { 'pl-4 pr-8': !$slots.icon && (ruesSearchbar || searchresultSearchbar) },
        { 'pt-6 pb-1 font-semibold rounded-xl min-h-[62px]': props.label && (ruesSearchbar || searchresultSearchbar) },
        { 'border-0 bg-white focus:outline-hidden focus:ring-1 focus:ring-transparent': ruesSearchbar || searchresultSearchbar},
        { 'lg:pt-5 lg:min-h-[46px]': props.label && searchresultSearchbar },
        'w-full'
      ]"
      @change="$emit('update:modelValue', $event.target.options[$event.target.selectedIndex]._value);"
      @click="toggleActiveClass"
    >
      <slot />
    </select>
  </div>
</template>
<script setup lang="ts">
import type { DesignVariant } from '~/types/ui/DesignVariant'
import { isDesignVariant } from '~/types/ui/DesignVariant'

const props = defineProps({
  design: {
    type: String as PropType<DesignVariant>,
    default: 'normal',
    validator: (value: string) => isDesignVariant(value)
  },
  label: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: ''
  },
  labelPosition: {
    type: String,
    default: 'inside'
  },
  name: {
    type: String,
    default: ''
  },
  ariaLabel: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: null
  },
  modelValue: {
    type: [String, Number, Array],
    default: ''
  },
  error: {
    type: Boolean,
    default: false
  },
  errorLevel: {
    type: String,
    default: 'error'
  },
  width: {
    type: String,
    default: 'w-full'
  }
})

const generatedId = useComponentId('select')
const finalId = computed(() => props.id ?? generatedId)

// Label choices
const label = computed(() => props.label === 'normal')
const labelup = computed(() => props.label === 'up')

const labeltop = computed(() => props.labelPosition === 'top')
const labelinside = computed(() => props.labelPosition === 'inside')

const normal = computed(() => props.design === 'normal')
const slim = computed(() => props.design === 'slim')
const pill = computed(() => props.design === 'pill')
const xs = computed(() => props.design === 'xs')
const greypill = computed(() => props.design === 'greypill')
const ruesSearchbar = computed(() => props.design === 'ruesSearchbar')
const searchresultSearchbar = computed(() => props.design === 'searchresultSearchbar')

const select = ref(null)
const isActive = ref(false)
const isWatcherTriggered = ref(false)

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  const elem = select.value
  elem.value = props.modelValue
  elem.addEventListener('blur', handleBlur)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  const elem = select.value
  elem.removeEventListener('blur-sm', handleBlur)
})

const toggleActiveClass = () => {
  if (!isWatcherTriggered.value) {
    isActive.value = !isActive.value
  }
}
const handleScroll = () => {
  isActive.value = false
}
const handleBlur = () => {
  isActive.value = false
}
watch(() => props.modelValue, () => {
  isWatcherTriggered.value = true
  isActive.value = false
  setTimeout(() => {
    isWatcherTriggered.value = false
  }, 0)
})

defineEmits<{(e: 'update:modelValue', value: string|number): void}>()
</script>
<style scoped>
@reference "~/assets/css/main.css";
.custom-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  -o-appearance: none;
  appearance: none;
  background-image: url('/svg/icons/select-arrow-uiselect.svg');
  background-size: 12px 12px;
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
}
.select-active {
  @apply border-petrol-500 outline-hidden ring-1 ring-petrol-500;
}
.select-no-active {
  @apply border-neutral-200 focus:border-neutral-200 focus:ring-0 focus:ring-neutral-200 focus:outline-hidden outline-hidden;
}
</style>
